import { useMemo } from "react";

const DAY_START_MIN = 8 * 60;   // 08:00
const DAY_END_MIN = 20 * 60;    // 20:00
const STEP = 15;                // minutes per row
const ROW_H = 22;               // px per row
const ROWS = (DAY_END_MIN - DAY_START_MIN) / STEP;

const WEEKDAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const DAY_LABELS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const minToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const addDays = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

function breaksForStaffDate(breaks, staffId, dateIso) {
  const wk = WEEKDAY_KEYS[new Date(dateIso + "T00:00:00").getDay()];
  return breaks
    .filter((b) => b.staff_id === staffId)
    .filter((b) => {
      if (b.type === "recurring") return b.weekday === "all" || b.weekday === wk;
      const de = b.date_end || b.date_start;
      return b.date_start && b.date_start <= dateIso && dateIso <= de;
    })
    .map((b) => ({
      id: b.id,
      oneoff: b.type === "oneoff",
      label: b.title || (b.type === "recurring" ? "Pause" : "Bloqué"),
      startMin: b.all_day ? DAY_START_MIN : toMin(b.time_start),
      endMin: b.all_day ? DAY_END_MIN : toMin(b.time_end),
    }));
}

export function AgendaGrid({ viewMode, viewDate, staff, staffFilter, appts, breaks, onMove, onBlock, onUnblock, onCancel }) {
  const activeStaff = staff.filter((s) => s.active !== false);

  const columns = useMemo(() => {
    if (viewMode === "week") {
      return Array.from({ length: 7 }, (_, i) => {
        const iso = addDays(viewDate, i);
        const d = new Date(iso + "T00:00:00");
        return { key: iso, kind: "day", dateIso: iso, label: `${DAY_LABELS[d.getDay()]} ${d.getDate()}` };
      });
    }
    const list = staffFilter === "all" ? activeStaff : activeStaff.filter((s) => s.id === staffFilter);
    return list.map((s) => ({ key: s.id, kind: "staff", staffId: s.id, dateIso: viewDate, label: s.name, color: s.color }));
  }, [viewMode, viewDate, staff, staffFilter]);

  const staffColor = (id) => staff.find((s) => s.id === id)?.color || "#C5A059";

  const apptsForColumn = (col) => appts.filter((a) => {
    if (a.status === "cancelled") return false;
    if (col.kind === "staff") return a.staff_id === col.staffId && a.date === viewDate;
    return a.date === col.dateIso && (staffFilter === "all" || a.staff_id === staffFilter);
  });

  const breaksForColumn = (col) => {
    if (col.kind === "staff") return breaksForStaffDate(breaks, col.staffId, viewDate);
    // week view: show breaks for the filtered staff only (avoid clutter when "all")
    if (staffFilter === "all") return [];
    return breaksForStaffDate(breaks, staffFilter, col.dateIso);
  };

  const rowFromClientY = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const idx = Math.max(0, Math.min(ROWS - 1, Math.floor(y / ROW_H)));
    return DAY_START_MIN + idx * STEP;
  };

  const handleDrop = (e, col) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/appt");
    if (!id) return;
    const startMin = rowFromClientY(e);
    const start_time = minToTime(startMin);
    if (col.kind === "staff") onMove(id, { date: viewDate, start_time, staff_id: col.staffId });
    else onMove(id, { date: col.dateIso, start_time });
  };

  const handleCellClick = (e, col) => {
    const startMin = rowFromClientY(e);
    const start_time = minToTime(startMin);
    const end_time = minToTime(Math.min(DAY_END_MIN, startMin + STEP));
    const staffId = col.kind === "staff" ? col.staffId : staffFilter;
    if (!staffId || staffId === "all") return; // need a specific staff to block in week view
    if (window.confirm(`Bloquer le créneau ${start_time}–${end_time} ?`)) {
      onBlock({ staff_id: staffId, type: "oneoff", date_start: col.dateIso, date_end: col.dateIso, all_day: false, time_start: start_time, time_end: end_time, title: "Bloqué" });
    }
  };

  const hourLabels = [];
  for (let m = DAY_START_MIN; m <= DAY_END_MIN; m += 60) hourLabels.push(m);

  return (
    <div className="overflow-x-auto border border-[#2C2A26]" data-testid="agenda-grid">
      <div style={{ display: "grid", gridTemplateColumns: `64px repeat(${columns.length}, minmax(140px, 1fr))`, minWidth: 64 + columns.length * 140 }}>
        {/* Header */}
        <div className="bg-[#141311] border-b border-r border-[#2C2A26] sticky top-0 z-20" style={{ height: 44 }} />
        {columns.map((col) => (
          <div key={col.key} className="bg-[#141311] border-b border-r border-[#2C2A26] flex items-center gap-2 px-3 sticky top-0 z-20" style={{ height: 44 }} data-testid={`agenda-col-${col.key}`}>
            {col.kind === "staff" && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: col.color }} />}
            <span className="text-xs uppercase tracking-[0.15em] text-[#F4F0EA] truncate">{col.label}</span>
          </div>
        ))}

        {/* Time axis */}
        <div className="relative border-r border-[#2C2A26]" style={{ gridColumn: 1, height: ROWS * ROW_H }}>
          {hourLabels.map((m) => (
            <div key={m} className="absolute right-2 text-[10px] text-[#6B6760]" style={{ top: ((m - DAY_START_MIN) / STEP) * ROW_H - 6 }}>{minToTime(m)}</div>
          ))}
        </div>

        {/* Columns */}
        {columns.map((col) => (
          <div
            key={col.key}
            className="relative border-r border-[#2C2A26]"
            style={{ height: ROWS * ROW_H }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, col)}
            onClick={(e) => handleCellClick(e, col)}
            data-testid={`agenda-colbody-${col.key}`}
          >
            {/* hour gridlines */}
            {Array.from({ length: ROWS }).map((_, i) => (
              <div key={i} className="absolute left-0 right-0" style={{ top: i * ROW_H, height: ROW_H, borderTop: i % 4 === 0 ? "1px solid #2C2A26" : "1px solid #1A1916" }} />
            ))}

            {/* breaks */}
            {breaksForColumn(col).map((b, i) => {
              const top = ((Math.max(DAY_START_MIN, b.startMin) - DAY_START_MIN) / STEP) * ROW_H;
              const h = ((Math.min(DAY_END_MIN, b.endMin) - Math.max(DAY_START_MIN, b.startMin)) / STEP) * ROW_H;
              if (h <= 0) return null;
              return (
                <div key={`b${i}`} className="absolute left-1 right-1 text-[10px] text-[#A39E93] flex items-center justify-between px-2 overflow-hidden" style={{ top, height: h, background: "repeating-linear-gradient(45deg,#1C1B18,#1C1B18 6px,#161512 6px,#161512 12px)", border: "1px dashed #3A382F" }} onClick={(e) => e.stopPropagation()} data-testid={`agenda-break-${b.id}`}>
                  <span className="truncate">{b.label}</span>
                  {b.oneoff && <button onClick={(e) => { e.stopPropagation(); onUnblock(b.id); }} className="text-[#8C3131] ml-1 shrink-0" data-testid={`agenda-unblock-${b.id}`}>✕</button>}
                </div>
              );
            })}

            {/* appointments */}
            {apptsForColumn(col).map((a) => {
              const sMin = toMin(a.start_time);
              const eMin = toMin(a.end_time);
              const top = ((sMin - DAY_START_MIN) / STEP) * ROW_H;
              const h = Math.max(ROW_H, ((eMin - sMin) / STEP) * ROW_H);
              return (
                <div
                  key={a.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("text/appt", a.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute left-1 right-1 rounded-sm px-2 py-1 overflow-hidden cursor-grab active:cursor-grabbing group"
                  style={{ top: top + 1, height: h - 2, backgroundColor: `${staffColor(a.staff_id)}22`, borderLeft: `3px solid ${staffColor(a.staff_id)}` }}
                  data-testid={`agenda-appt-${a.id}`}
                  title={`${a.client_first_name} ${a.client_last_name} · ${a.service_name} · ${a.start_time}-${a.end_time}`}
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className="text-[11px] font-medium text-[#F4F0EA] truncate">{a.start_time} {a.client_first_name} {a.client_last_name}</span>
                    <button onClick={(e) => { e.stopPropagation(); onCancel(a.id); }} className="text-[#A39E93] hover:text-[#8C3131] text-xs opacity-0 group-hover:opacity-100 shrink-0" data-testid={`agenda-cancel-${a.id}`}>✕</button>
                  </div>
                  {h > 34 && <div className="text-[10px] text-[#A39E93] truncate">{a.service_name}{viewMode === "week" && a.staff_name ? ` · ${a.staff_name}` : ""}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
